import { useState, useCallback } from 'react';
import { Drawer, IconButton, List, ListItemButton, ListItemText, Box } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation } from 'react-router-dom';

// Constants
const PAGES = ["Home", "About", "Services", "Contact"];

// Extracted styles for better maintainability
const styles = {
  drawer: {
    '& .MuiDrawer-paper': {
      width: {
        xs: '66.666%', // 2/3 of the screen width for extra small screens
        sm: '300px'    // Default width for larger screens
      },
      backgroundColor: '#e6e4df',
      fontFamily: 'Roboto, Arial, sans-serif',
    }
  },
  list: {
    backgroundColor: '#e6e4df',
    height: '100%',
    padding: '1rem 0'
  },
  listItem: {
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      transition: 'background-color 0.3s ease'
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.12)'
      }
    }
  },
  listItemText: {
    fontFamily: 'Roboto, Arial, sans-serif',
    color: 'black',
    fontWeight: 500,
    fontSize: '2.2rem',
    textAlign: 'center'
  },
  menuButton: {
    color: 'black',
    marginLeft: 'auto',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
    padding: '10px',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      transform: 'scale(1.1)'
    },
    '&:focus-visible': {
      outline: '2px solid #000',
      outlineOffset: '2px'
    }
  },
  menuIcon: {
    fontSize: '2rem'
  }
};

/**
 * DrawerComp component that displays a mobile navigation menu
 * @returns {JSX.Element} The rendered DrawerComp component
 */
const DrawerComp = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const location = useLocation();

  // Memoize the close handler to prevent unnecessary re-renders
  const handleClose = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  // Memoize the toggle handler
  const handleToggle = useCallback(() => {
    setOpenDrawer(prev => !prev);
  }, []);

  // Get current path for active item
  const currentPath = location.pathname === '/' ? 'Home' : location.pathname.slice(1).charAt(0).toUpperCase() + location.pathname.slice(2);

  return (
    <Box component="nav" aria-label="Mobile navigation">
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleClose}
        sx={styles.drawer}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
      >
        <List sx={styles.list}>
          {PAGES.map((page) => (
            <ListItemButton
              key={page}
              component={Link}
              to={page === 'Home' ? '/' : `/${page.toLowerCase()}`}
              onClick={handleClose}
              selected={currentPath === page}
              sx={styles.listItem}
              aria-current={currentPath === page ? 'page' : undefined}
            >
              <ListItemText
                primary={page}
                primaryTypographyProps={{
                  sx: styles.listItemText
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
      <IconButton
        onClick={handleToggle}
        sx={styles.menuButton}
        aria-label="Open navigation menu"
        aria-expanded={openDrawer}
        aria-controls="mobile-menu"
      >
        <MenuIcon sx={styles.menuIcon} />
      </IconButton>
    </Box>
  );
};

export default DrawerComp;